<template>
    <div class="comm-page">
        <!-- Sticky Top Bar -->
        <div class="comm-topbar">
            <div class="comm-topbar-left">
                <button class="comm-back-btn" @click="$router.push('/communication-groups')">
                    <i class="fas fa-arrow-left"></i>
                </button>
                <div class="comm-group-avatar" v-if="communicationGroup">
                    <img
                        v-if="communicationGroup.thumbnailUrl"
                        :src="getAssetUrlPath(communicationGroup.thumbnailUrl)"
                        @error="onAvatarError"
                        alt=""
                    />
                    <i v-else class="fas fa-users"></i>
                </div>
                <div class="comm-topbar-info" v-if="communicationGroup">
                    <h2 class="comm-topbar-title">{{ communicationGroup.name }}</h2>
                    <span class="comm-topbar-meta">
                        <i class="fas fa-comment-dots"></i>
                        {{ messages.length }} message{{ messages.length !== 1 ? 's' : '' }}
                    </span>
                </div>
            </div>
            <div class="comm-topbar-right">
                <button
                    v-if="messages.length && !showConfirmationMessage"
                    class="comm-clear-btn"
                    @click="handleClear"
                >
                    <i class="fas fa-trash-alt"></i>
                    <span class="comm-clear-label">Clear All</span>
                </button>
            </div>
        </div>

        <!-- Confirmation Banner -->
        <div v-if="showConfirmationMessage" class="comm-confirm-banner">
            <div class="comm-confirm-content">
                <i class="fas fa-exclamation-triangle comm-confirm-icon"></i>
                <span>Delete all messages? This action cannot be undone.</span>
            </div>
            <div class="comm-confirm-actions">
                <button class="comm-confirm-yes" @click="confirmClearMessages" :disabled="deleting">
                    {{ deleting ? "Deleting..." : "Yes, delete all" }}
                </button>
                <button class="comm-confirm-no" @click="showConfirmationMessage = false">Cancel</button>
            </div>
        </div>

        <!-- Main Content -->
        <div class="comm-body">
            <!-- Loading -->
            <div v-if="loading && !messages.length" class="comm-loading">
                <div class="comm-spinner"></div>
                <span>Loading messages...</span>
            </div>

            <!-- Empty State -->
            <div v-else-if="!loading && messages.length === 0" class="comm-empty">
                <div class="comm-empty-icon">
                    <i class="fas fa-comments"></i>
                </div>
                <h3>No messages yet</h3>
                <p>Messages in this group will appear here</p>
            </div>

            <!-- Messages Grid -->
            <div v-else class="comm-messages-grid">
                <!-- Load More (top) -->
                <div class="comm-load-more" v-show="!completed">
                    <button class="comm-load-btn" @click="loadMoreMessages" :disabled="loading">
                        <i class="fas fa-history" v-if="!loading"></i>
                        <div class="comm-spinner-sm" v-else></div>
                        {{ loading ? "Loading..." : "Load older messages" }}
                    </button>
                </div>

                <div
                    class="comm-message-card"
                    v-for="(message, idx) in messages"
                    :key="message.id"
                    :style="{ animationDelay: `${idx * 0.03}s` }"
                >
                    <div class="comm-msg-sender-col">
                        <div class="comm-sender-avatar">
                            <img
                                v-if="getSenderImage(message)"
                                :src="getSenderImage(message)"
                                @error="onAvatarError"
                                alt=""
                            />
                            <span v-else-if="getSenderInitials(message)" class="comm-avatar-initials">
                                {{ getSenderInitials(message) }}
                            </span>
                            <img v-else :src="fallbackAvatar" alt="" />
                        </div>
                    </div>
                    <div class="comm-msg-content-col">
                        <div class="comm-msg-meta">
                            <span class="comm-sender-name">{{ getSenderName(message) }}</span>
                            <span class="comm-msg-time">
                                {{ formatIndianDate(message.createdAt) }}
                            </span>
                        </div>

                        <div class="comm-msg-text" v-if="message.text">{{ message.text }}</div>

                        <!-- Media -->
                        <div class="comm-msg-media" v-if="message.mediaSrc">
                            <div v-if="message.mediaIsImage" class="comm-msg-image">
                                <a target="_blank" :href="getAssetUrlPath(message.mediaSrc)">
                                    <img
                                        :src="getAssetUrlPath(message.mediaSrc)"
                                        @error="onImageError"
                                        alt="Attached image"
                                    />
                                </a>
                            </div>
                            <a v-else class="comm-msg-file" target="_blank" :href="getAssetUrlPath(message.mediaSrc)">
                                <i class="fas fa-paperclip"></i> View attached file
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../store/permission"
import { formatIndianDate, getAssetUrlPath } from "../../utils/common"
import fallbackAvatarImg from "@/assets/user.png"

export default {
    data() {
        return {
            PERMISSION_CODES,
            loading: false,
            messages: [],
            showConfirmationMessage: false,
            page: 1,
            communicationGroup: null,
            completed: false,
            deleting: false,
            fallbackAvatar: fallbackAvatarImg,
        }
    },
    components: {},
    async mounted() {
        this.fetchMessages()
    },
    methods: {
        getAssetUrlPath,
        formatIndianDate,
        // --- Sender helpers (handles multiple possible API shapes) ---
        _getSender(message) {
            return message.sender || message.user || message.employee || message.createdBy || null
        },
        getSenderName(message) {
            const s = this._getSender(message)
            if (!s) return "Unknown"
            const first = s.firstName || s.first_name || s.name || ""
            const last = s.lastName || s.last_name || ""
            const full = `${first} ${last}`.trim()
            return full || s.email || s.username || "Unknown"
        },
        getSenderInitials(message) {
            const s = this._getSender(message)
            if (!s) return ""
            const first = (s.firstName || s.first_name || s.name || "").charAt(0)
            const last = (s.lastName || s.last_name || "").charAt(0)
            return (first + last).toUpperCase() || ""
        },
        getSenderImage(message) {
            const s = this._getSender(message)
            if (!s) return null
            const img = s.profileImage || s.profile_image || s.avatar || s.thumbnailUrl || null
            if (!img) return null
            return getAssetUrlPath(img)
        },
        onAvatarError(e) {
            e.target.src = this.fallbackAvatar
        },
        onImageError(e) {
            e.target.classList.add('comm-img-broken')
            e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" fill="%23cbd5e1"><rect width="120" height="120" rx="8" fill="%23f1f5f9"/><text x="50%" y="54%" font-size="13" text-anchor="middle" dominant-baseline="middle" fill="%2394a3b8" font-family="sans-serif">Image not found</text></svg>'
        },
        handleClear() {
            this.showConfirmationMessage = true
        },
        async loadMoreMessages() {
            this.page++
            try {
                this.loading = true
                const res = await http.get(
                    `/communication-messages?is_group=true&page=${this.page}&limit=10&chat_id=${this.$route.params.id}&reverse_messages=false`
                )
                this.messages = [...this.messages, ...res.data.result]
                if (res.data.result.length < 10) {
                    this.completed = true
                }
            } catch (err) {
                console.log("error in getting messages", err)
            }
            this.loading = false
        },
        async confirmClearMessages() {
            try {
                if (this.deleting) return
                this.deleting = true
                // alert("Are you sure u want to delete it??");
                const response = await http.delete(`/communication-messages/${this.$route.params.id}`)
                if (response.status === 200) {
                    this.messages = []
                    this.showConfirmationMessage = false
                    this.$toast.success("Deleted successfully")
                    this.$router.push("/communication-groups")
                } else {
                    this.$toast.error("Failed to delete messages")
                }
            } catch (err) {
                console.log("error in clearing messages", err)
            }
            this.deleting = false
        },
        async fetchMessages() {
            this.loading = true
            try {
                const res = await http.get(`/communication-messages?is_group=true&page=1&limit=10&chat_id=${this.$route.params.id}`)
                this.communicationGroup = res.data.communicationGroup
                this.messages = res.data.result
                // console.log(this.messages);
            } catch (err) {
                console.log("error in getting messages", err)
            }
            this.loading = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

/* ========== PAGE LAYOUT ========== */
.comm-page {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 120px);
    animation: commFadeIn 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes commFadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ========== TOP BAR ========== */
.comm-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 24px;
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusLg;
    margin-bottom: 16px;
    box-shadow: $shadowSm;
    position: sticky;
    top: 0;
    z-index: 10;
}
.comm-topbar-left {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex: 1;
}
.comm-back-btn {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid $gray200;
    border-radius: $radiusSm;
    background: white;
    color: $gray500;
    font-size: 14px;
    cursor: pointer;
    transition: all $transitionFast;
    flex-shrink: 0;

    &:hover {
        background: $gray50;
        border-color: $gray300;
        color: $gray800;
        transform: translateX(-1px);
    }
}
.comm-group-avatar {
    width: 42px;
    height: 42px;
    border-radius: $radiusMd;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 5%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 94, 137, 0.1);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}
.comm-topbar-info {
    min-width: 0;
    flex: 1;
}
.comm-topbar-title {
    font-size: 17px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
    line-height: 1.25;
    letter-spacing: -0.015em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.comm-topbar-meta {
    font-size: 12px;
    color: $gray400;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 2px;

    i { font-size: 11px; }
}
.comm-topbar-right {
    flex-shrink: 0;
}
.comm-clear-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 8px 16px;
    border: 1px solid rgba(239, 68, 68, 0.25);
    border-radius: $radiusSm;
    background: #fef2f2;
    color: $danger;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all $transitionFast;

    &:hover {
        background: #fee2e2;
        border-color: rgba(239, 68, 68, 0.45);
    }
}

/* ========== CONFIRM BANNER ========== */
.comm-confirm-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 20px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: $radiusMd;
    margin-bottom: 16px;
}
.comm-confirm-content {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
    color: #92400e;
}
.comm-confirm-icon {
    color: #f59e0b;
    font-size: 16px;
}
.comm-confirm-actions {
    display: flex;
    gap: 8px;
}
.comm-confirm-yes {
    padding: 7px 18px;
    border: none;
    border-radius: $radiusSm;
    background: $danger;
    color: white;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;

    &:hover { background: #dc2626; }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
}
.comm-confirm-no {
    padding: 7px 18px;
    border: 1px solid $gray200;
    border-radius: $radiusSm;
    background: white;
    color: $gray600;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all $transitionFast;

    &:hover { background: $gray50; }
}

/* ========== BODY ========== */
.comm-body {
    flex: 1;
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusLg;
    padding: 24px;
    box-shadow: $shadowSm;
}

/* ========== LOADING ========== */
.comm-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 80px 20px;

    span {
        font-size: 13px;
        color: $gray400;
        font-weight: 500;
    }
}
.comm-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: commSpin 0.7s linear infinite;
}
.comm-spinner-sm {
    width: 16px;
    height: 16px;
    border: 2px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: commSpin 0.7s linear infinite;
}
@keyframes commSpin { to { transform: rotate(360deg); } }

/* ========== EMPTY STATE ========== */
.comm-empty {
    text-align: center;
    padding: 80px 20px;

    h3 {
        font-size: 17px;
        font-weight: 700;
        color: $gray600;
        margin: 0 0 6px;
    }
    p {
        font-size: 13px;
        color: $gray400;
        margin: 0;
    }
}
.comm-empty-icon {
    width: 72px;
    height: 72px;
    border-radius: $radiusFull;
    background: $gray50;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    border: 1px solid $gray100;

    i {
        font-size: 28px;
        color: $gray300;
    }
}

/* ========== MESSAGES GRID ========== */
.comm-messages-grid {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.comm-message-card {
    display: flex;
    gap: 14px;
    padding: 16px;
    border-radius: $radiusMd;
    animation: msgSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
    transition: background $transitionFast;

    &:hover {
        background: $gray50;
    }
}
@keyframes msgSlideIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ---- Sender Column ---- */
.comm-msg-sender-col {
    flex-shrink: 0;
    padding-top: 2px;
}
.comm-sender-avatar {
    width: 40px;
    height: 40px;
    border-radius: $radiusFull;
    overflow: hidden;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 5%));
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
    box-shadow: 0 1px 4px rgba(0,0,0,0.07);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}
.comm-avatar-initials {
    font-size: 13px;
    font-weight: 700;
    color: $primaryColor;
    line-height: 1;
    letter-spacing: 0.02em;
}

/* ---- Content Column ---- */
.comm-msg-content-col {
    flex: 1;
    min-width: 0;
}
.comm-msg-meta {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 5px;
}
.comm-sender-name {
    font-size: 14px;
    font-weight: 650;
    color: $gray800;
    line-height: 1;
}
.comm-msg-time {
    font-size: 11px;
    color: $gray400;
    font-weight: 500;
    white-space: nowrap;
}
.comm-msg-text {
    font-size: 14px;
    color: $gray600;
    line-height: 1.6;
    word-break: break-word;
}

/* ---- Media ---- */
.comm-msg-media {
    margin-top: 10px;
}
.comm-msg-image {
    display: inline-block;

    img {
        max-width: 280px;
        max-height: 220px;
        width: auto;
        height: auto;
        object-fit: cover;
        border-radius: $radiusMd;
        border: 1px solid $gray200;
        transition: all $transitionBase;
        cursor: pointer;

        &:hover {
            box-shadow: $shadowLg;
            transform: scale(1.01);
        }

        &.comm-img-broken {
            width: 160px;
            height: 100px;
            object-fit: contain;
            border-color: $gray100;
            background: $gray50;
            cursor: default;

            &:hover {
                box-shadow: none;
                transform: none;
            }
        }
    }
}
.comm-msg-file {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: 1px solid $gray200;
    border-radius: $radiusSm;
    background: $gray50;
    color: $primaryColor;
    font-size: 13px;
    font-weight: 500;
    transition: all $transitionFast;
    text-decoration: none;

    &:hover {
        background: $primaryLight;
        border-color: rgba(0, 94, 137, 0.25);
        text-decoration: none;
    }
}

/* ========== LOAD MORE ========== */
.comm-load-more {
    text-align: center;
    padding: 8px 0 16px;
}
.comm-load-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    border: 1px dashed $gray200;
    border-radius: $radiusFull;
    background: white;
    color: $primaryColor;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all $transitionFast;

    &:hover {
        background: $primaryLight;
        border-color: rgba(0, 94, 137, 0.25);
        border-style: solid;
    }
    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
}

/* ========== RESPONSIVE ========== */
@media (max-width: 768px) {
    .comm-topbar {
        padding: 12px 16px;
        gap: 10px;
        border-radius: $radiusMd;
    }
    .comm-topbar-left { gap: 10px; }
    .comm-group-avatar {
        width: 36px;
        height: 36px;
        font-size: 15px;
    }
    .comm-topbar-title { font-size: 15px; }
    .comm-clear-label { display: none; }
    .comm-clear-btn { padding: 8px 10px; }
    .comm-body {
        padding: 16px;
        border-radius: $radiusMd;
    }
    .comm-message-card {
        padding: 12px 8px;
        gap: 10px;
    }
    .comm-sender-avatar {
        width: 34px;
        height: 34px;
    }
    .comm-msg-image img {
        max-width: 200px;
        max-height: 160px;
    }
}
@media (max-width: 480px) {
    .comm-topbar {
        padding: 10px 12px;
        border-radius: $radiusSm;
    }
    .comm-back-btn {
        width: 34px;
        height: 34px;
        font-size: 13px;
    }
    .comm-group-avatar {
        width: 32px;
        height: 32px;
        font-size: 13px;
        border-radius: $radiusSm;
    }
    .comm-topbar-title { font-size: 14px; }
    .comm-body {
        padding: 10px;
        border-radius: $radiusSm;
    }
    .comm-message-card {
        padding: 10px 4px;
        gap: 8px;
    }
    .comm-sender-avatar {
        width: 30px;
        height: 30px;
    }
    .comm-sender-name { font-size: 13px; }
    .comm-msg-text { font-size: 13px; }
    .comm-msg-image img {
        max-width: 100%;
        max-height: 180px;
    }
    .comm-confirm-banner {
        flex-direction: column;
        align-items: flex-start;
        padding: 12px 14px;
    }
}
</style>
